/**
 * Lyrics Compositor Module
 *
 * Handles lyrics rendering and compositing during video export.
 * Manages the lyrics timeline and frame-by-frame compositing.
 * Extracted from exporter.js for better separation of concerns.
 */

/**
 * LyricsCompositor - manages lyrics rendering during export
 */
export class LyricsCompositor {
  /**
   * @param {Object} options
   * @param {Object} options.lyricsRenderer - LyricsRenderer instance
   * @param {number} options.width - Canvas width
   * @param {number} options.height - Canvas height
   * @param {Array} options.items - Playlist items with lyrics
   */
  constructor(options) {
    this.lyricsRenderer = options.lyricsRenderer;
    this.width = options.width;
    this.height = options.height;

    // Create offscreen canvases for compositing
    this.lyricsCanvas = new OffscreenCanvas(this.width, this.height);
    this.lyricsCtx = this.lyricsCanvas.getContext('2d');
    this.compositeCanvas = new OffscreenCanvas(this.width, this.height);
    this.compositeCtx = this.compositeCanvas.getContext('2d');

    // Build lyrics timeline
    this.timeline = this.buildTimeline(options.items);
  }

  /**
   * Build lyrics timeline for concatenated tracks
   * @param {Array} items - Playlist items with lyrics
   * @returns {Array} Timeline entries with start/end times and scroll params
   */
  buildTimeline(items) {
    const timeline = [];
    let cumulativeTime = 0;

    for (const item of items) {
      if (!item.included) continue;

      if (item.lyrics && item.duration) {
        // Calculate scroll parameters for this track
        // Pass alignedLyrics if available for time-synced highlighting
        const scrollParams = this.lyricsRenderer.calculateScrollParams(
          item.lyrics,
          item.duration,
          this.width,
          this.height,
          item.alignedLyrics
        );

        timeline.push({
          startTime: cumulativeTime,
          endTime: cumulativeTime + item.duration,
          scrollParams,
          lyrics: item.lyrics,
        });
      }

      cumulativeTime += item.duration || 0;
    }

    return timeline;
  }

  /**
   * Check if lyrics are available for any track
   * @returns {boolean}
   */
  hasLyrics() {
    return this.timeline.length > 0;
  }

  /**
   * Get timeline entry for a given time
   * @param {number} globalTime - Current time in seconds
   * @returns {Object|null} Timeline entry or null
   */
  getTimelineEntry(globalTime) {
    for (const entry of this.timeline) {
      if (globalTime >= entry.startTime && globalTime < entry.endTime) {
        return entry;
      }
    }
    return null;
  }

  /**
   * Render lyrics at a specific time onto lyrics canvas
   * @param {number} globalTime - Current time in seconds
   */
  renderLyrics(globalTime) {
    // Clear lyrics canvas
    this.lyricsCtx.clearRect(0, 0, this.width, this.height);

    const entry = this.getTimelineEntry(globalTime);
    if (entry) {
      const localTime = globalTime - entry.startTime;
      this.lyricsRenderer.render(
        this.lyricsCtx,
        this.lyricsCanvas,
        entry.scrollParams,
        localTime
      );
    }
  }

  /**
   * Composite visualization frame with lyrics overlay
   * @param {OffscreenCanvas|HTMLCanvasElement} visualCanvas - Source visualization
   * @param {number} globalTime - Current time in seconds
   * @returns {OffscreenCanvas} Composited canvas
   */
  compositeFrame(visualCanvas, globalTime) {
    // Render lyrics for this frame
    this.renderLyrics(globalTime);

    // Draw visualization first
    this.compositeCtx.drawImage(visualCanvas, 0, 0);

    // Draw lyrics overlay on top
    this.compositeCtx.drawImage(this.lyricsCanvas, 0, 0);

    return this.compositeCanvas;
  }

  /**
   * Get the composite canvas (for direct access if needed)
   * @returns {OffscreenCanvas}
   */
  getCompositeCanvas() {
    return this.compositeCanvas;
  }

  /**
   * Get the lyrics canvas for GPU-side compositing (WebGPU mode).
   * Renders lyrics to the lyrics canvas and returns it directly.
   * The caller is responsible for compositing this onto the visualization.
   * This avoids GPU readback by not calling drawImage() on the WebGPU canvas.
   * @param {number} globalTime - Current time in seconds
   * @returns {OffscreenCanvas} The lyrics canvas (transparent background with lyrics)
   */
  getLyricsCanvas(globalTime) {
    this.renderLyrics(globalTime);
    return this.lyricsCanvas;
  }

  /**
   * Clean up resources
   */
  dispose() {
    // OffscreenCanvas doesn't need explicit cleanup, but clear references
    this.lyricsCanvas = null;
    this.lyricsCtx = null;
    this.compositeCanvas = null;
    this.compositeCtx = null;
    this.timeline = [];
  }
}

/**
 * Create a LyricsCompositor if lyrics are enabled
 * @param {Object} options
 * @param {Object} options.lyricsRenderer - LyricsRenderer instance
 * @param {boolean} options.lyricsEnabled - Whether lyrics are enabled
 * @param {number} options.width - Canvas width
 * @param {number} options.height - Canvas height
 * @param {Array} options.items - Playlist items
 * @param {string} options.contextType - Visualizer context type
 * @returns {LyricsCompositor|null}
 */
export function createLyricsCompositor(options) {
  const { lyricsRenderer, lyricsEnabled, width, height, items, contextType } = options;

  // Skip if lyrics not enabled or no renderer
  if (!lyricsEnabled || !lyricsRenderer) {
    return null;
  }

  // Check if any items have lyrics
  const hasLyrics = items.some(item => item.included && item.lyrics);
  if (!hasLyrics) {
    return null;
  }

  // Log info for WebGPU (now uses GPU-side compositing)
  if (contextType === 'webgpu') {
    console.log('[Lyrics] Using GPU-side compositing for WebGPU visualizer');
  }

  return new LyricsCompositor({
    lyricsRenderer,
    width,
    height,
    items,
  });
}
